import { useCallback, useMemo, useRef, useState } from 'react';
import { FormatListBulletedOutlined } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { RecommendationPageMode, CorrectionFormData, PlantingAdvice } from '../types';
import { CorrectionForm } from '../components/Forms/CorrectionForm/CorrectionForm';
import type { CorrectionFormRef } from '../components/Forms/CorrectionForm/CorrectionForm';
import { CORRECTION_TITLES, CORRECTION_SUBTITLES } from '../constants/correction.constants';
import { usePostAdviceCorrectionCreate } from '../../api/hooks/recommendations';
import { PanelActions } from '../components/PanelActions/PanelActions';
import styles from './CorrectionAdvicePage.module.css';

export interface CorrectionAdvicePageProps {
	onBack: () => void;
	initialMode?: RecommendationPageMode;
	initialData?: CorrectionFormData;
	viewData?: PlantingAdvice;
	adviceId?: string;
	onDelete?: () => Promise<void> | void;
	onStart?: (data: CorrectionFormData) => Promise<void> | void;
}

const getFormValue = (value: unknown): string => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const id = record._id ?? record.id ?? record.name;
		return typeof id === 'string' ? id : '';
	}
	return String(value);
};

export const CorrectionAdvicePage = ({
	onBack,
	initialMode = 'add',
	initialData,
	viewData,
	adviceId,
	onDelete,
	onStart,
}: CorrectionAdvicePageProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const formRef = useRef<CorrectionFormRef>(null);

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const toast = useToast();
	const { mutateAsync: upsertCorrectionAdvice } = usePostAdviceCorrectionCreate();

	const handleSubmit = useCallback(
		async (data: CorrectionFormData) => {
			const persistedId = adviceId || viewData?._id;
			if (initialMode === 'edit' && persistedId && viewData) {
				setIsLoading(true);
				try {
					await upsertCorrectionAdvice({
						_id: persistedId,
						...data,
						formasAplicacao: viewData.formasAplicacao ?? {},
						...(viewData.note !== undefined ? { note: viewData.note } : {}),
						...(viewData.calagem
							? {
									calagem: {
										...viewData.calagem,
										product: getFormValue(viewData.calagem.product),
									},
								}
							: {}),
						...(viewData.adubacao
							? {
									adubacao: {
										...viewData.adubacao,
										products: viewData.adubacao.products.map((product) => ({
											...product,
											productId: product.productId || getFormValue(product.product),
										})),
									},
								}
							: {}),
						...(viewData.gessagem
							? {
									gessagem: {
										...viewData.gessagem,
										productId:
											viewData.gessagem.productId || getFormValue(viewData.gessagem.product),
									},
								}
							: {}),
						...(viewData.resultado ? { resultado: viewData.resultado } : {}),
						...(viewData.isUsed ? { isUsed: viewData.isUsed } : {}),
					});
					toast.success('Recomendação atualizada com sucesso!');
					onBack();
				} catch (error) {
					toast.error(
						error instanceof Error ? error.message : 'Erro ao atualizar recomendação.'
					);
				} finally {
					setIsLoading(false);
				}
			} else {
				setIsLoading(true);
				try {
					await onStart?.(data);
				} finally {
					setIsLoading(false);
				}
			}
		},
		[initialMode, adviceId, viewData, upsertCorrectionAdvice, toast, onBack, onStart]
	);

	const handleCancel = useCallback(async () => {
		if (!formRef.current?.isDirty?.()) {
			onBack();
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (confirmed) onBack();
	}, [confirm, onBack]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir recomendação',
			message: 'Tem certeza que deseja excluir esta recomendação? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (confirmed) {
			try {
				await onDelete?.();
				toast.success('Recomendação excluída com sucesso.');
			} catch {
				toast.error('Erro ao excluir recomendação.');
			}
		}
	}, [confirm, onDelete, toast]);

	const handleSave = useCallback(() => {
		formRef.current?.submit();
	}, []);

	const editInitialData: CorrectionFormData | undefined = useMemo(() => {
		if (initialMode === 'edit' && viewData) {
			return {
				name: viewData.name,
				prop: getFormValue(viewData.prop),
				area: getFormValue(viewData.area),
				sectors: viewData.sectors.map(getFormValue).filter(Boolean),
				soilAnalysis: getFormValue(viewData.soilAnalysis),
			};
		}
		return initialData;
	}, [initialMode, viewData, initialData]);

	const saveText = initialMode === 'add' ? 'Iniciar' : 'Salvar';

	return (
		<div className={styles.page}>
			<div className={styles.contentWrapper}>
				<PageHeader
					title={CORRECTION_TITLES[initialMode]}
					subtitle={CORRECTION_SUBTITLES[initialMode]}
					onBack={onBack}
				/>
				<div className={styles.cards}>
					<div className={styles.mainContent}>
						<FormCard
							icon={<FormatListBulletedOutlined />}
							title="Informações Gerais"
							className={styles.mainCard}
						>
							<CorrectionForm
								key={initialMode}
								ref={formRef}
								initialData={editInitialData}
								onSubmit={handleSubmit}
								onLoadingChange={setIsLoading}
							/>
						</FormCard>
						<PanelActions>
							<FormActions
								mode={initialMode}
								onCancel={handleCancel}
								onSave={handleSave}
								isLoading={isLoading}
								onDelete={onDelete ? handleDelete : undefined}
								saveText={saveText}
							/>
						</PanelActions>
					</div>
				</div>
			</div>

			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
